import { get, post } from "utilities/network";

export type Market = "NGN" | "GHS" | "KES";
export type ReferralStatus = "pending" | "successful" | "capped" | "rejected";
export type MarketConfig = {
  enabled: boolean;
  minimumTier: 1 | 2 | 3;
  airtimeData: { enabled: boolean; thresholdMinor: number };
  cryptoSwapThresholdUsdCents: number;
  cryptoTransactionTypes: {
    cryptoToCrypto: boolean;
    cryptoToFiat: boolean;
    sendFromCrypto: boolean;
    billsFromCrypto: boolean;
  };
  swapValuation:
    | { mode: "live"; fixedLocalMinorPerUsd: null }
    | { mode: "fixed"; fixedLocalMinorPerUsd: number };
  rewardAmountMinor: number;
  maxRewardsPerCycle: number;
  cycleDays: number;
};
export type Programme = {
  launchedAt: string | null;
  linkPrefix: string;
  markets: Record<Market, MarketConfig>;
};
export type ProgrammeSnapshot = {
  referralProgramme: Programme;
  version: string | null;
};
export type Page<T> = {
  results: T[];
  meta: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    totalPages: number;
    currentPage: number;
    nextPage: number | null;
  };
};
export type Progress = {
  tier: { reachedTier: number; requiredTier: number; done: boolean };
  airtimeData: {
    totalMinor: number;
    thresholdMinor: number;
    done: boolean;
  } | null;
  cryptoSwaps: {
    totalUsdCents: number;
    thresholdUsdCents: number;
    done: boolean;
  };
  allMet: boolean;
};
export type Party = {
  id: number;
  userName: string | null;
  firstName: string | null;
  email: string | null;
};
export type ReferralRecord = {
  id: number;
  referrerId: number;
  refereeId: number;
  referrerMarket: Market;
  refereeMarket: Market;
  status: ReferralStatus;
  reasonCode: string | null;
  createdAt: string;
  referrer: Party;
  referee: Party;
  onHold: boolean;
  openFlagCount: number;
  progress: Progress;
  rewardAmountMinor: number | null;
  rewardCurrency: Market | null;
  cycleId: number | null;
  rewardedAt: string | null;
  qualifiedAt: string | null;
  resolvedAt: string | null;
  rewardReversedAt: string | null;
  rewardReversalLienMinor: number | null;
};
export type RecordFilters = {
  status?: ReferralStatus;
  referrer?: string;
  referee?: string;
  onHold?: boolean;
  market?: Market;
  signedUpFrom?: string;
  signedUpBefore?: string;
};
export type ReviewItem = {
  referralId: number;
  referrerId: number;
  refereeId: number;
  referrerMarket: Market;
  refereeMarket: Market;
  createdAt: string;
  qualifiedAt: string | null;
  openFlags: {
    id: number;
    signal: "device" | "bank_account";
    matchedUserId: number;
    raisedStage: string;
    raisedAt: string;
  }[];
};
export type ReviewAction = "clear-hold" | "reject" | "reverse";
export type ActionResult = {
  referralId: number;
  status: ReferralStatus;
  currency?: Market;
  reversedMinor?: number;
  balanceDebitedMinor?: number;
  walletLienMinor?: number;
};
export type UserView = {
  user: {
    id: number;
    userName: string | null;
    firstName: string | null;
    country: string | null;
    market: Market;
  };
  referralCode: string | null;
  referralLink: string | null;
  eligibility: {
    eligibleAt: string | null;
    progress: Progress;
    marketPaused: boolean;
  };
  suspension: {
    suspendedAt: string;
    suspendedBy: number | null;
    reason: string | null;
  } | null;
  balance: {
    balanceMinor: number;
    lifetimeEarnedMinor: number;
    currency: Market;
  };
  cycle: {
    active: boolean;
    rewardsUsed: number;
    maxRewards: number;
    amountEarnedMinor: number;
    amountCapMinor: number;
    currency: Market;
    startAt: string | null;
    endAt: string | null;
    resetsAt: string | null;
  };
  referralCounts: Record<ReferralStatus, number>;
  referrals: Page<ReferralRecord>;
  referredBy: ReferralRecord | null;
  history: Page<{
    id: number;
    kind: string;
    amountMinor: number;
    currency: Market;
    balanceAfterMinor: number;
    createdAt: string;
    walletLienMinor: number;
  }>;
};
export type ReportFilters = { from: string; to: string; market?: Market };
export type ReferralReport = {
  from: string;
  to: string;
  markets: {
    market: Market;
    signUps: number;
    successful: number;
    capped: number;
    rejected: number;
    onHoldNow: number;
    rewards: {
      currency: Market;
      grossMinor: number;
      reversedMinor: number;
      netMinor: number;
      reversedCount: number;
    }[];
  }[];
  topReferrers: {
    referrerId: number;
    userName: string | null;
    firstName: string | null;
    email: string | null;
    market: Market;
    successful: number;
    capped: number;
    rejected: number;
    netRewardMinor: number;
    balanceCurrency: Market | null;
    balanceMinor: number | null;
  }[];
};
const recordsPath = "admin/transactions/referrals";
const programmePath = "admin/transactions/referral-programme";
const path = "admin/referrals";
export const referralQuery = (params: object) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") query.set(key, String(value));
  });
  return query.toString();
};
export const fetchProgramme = (): Promise<ProgrammeSnapshot> =>
  get(programmePath);
export const saveProgramme = async (
  input: ProgrammeSnapshot & { reason: string },
): Promise<ProgrammeSnapshot> => (await post(programmePath, input)).data;
export const fetchRecords = (
  filters: RecordFilters,
  page: number,
): Promise<Page<ReferralRecord>> =>
  get(`${recordsPath}?${referralQuery({ ...filters, page, limit: 20 })}`);
export const fetchReviewQueue = (page: number): Promise<Page<ReviewItem>> =>
  get(`${path}/review-queue?page=${page}&limit=20`);
export const reviewReferral = async (
  id: number,
  action: ReviewAction,
  reason: string,
): Promise<ActionResult> =>
  (await post(`${path}/${id}/${action}`, { reason })).data;
export const fetchReferralUser = (id: number): Promise<UserView> =>
  get(`${path}/users/${id}`);
export const changeSuspension = async (
  id: number,
  action: "suspend" | "reinstate",
  reason: string,
): Promise<{ userId: number; suspended: boolean }> =>
  (await post(`${path}/users/${id}/${action}`, { reason })).data;
export const fetchReport = (filters: ReportFilters): Promise<ReferralReport> =>
  get(`${path}/report?${referralQuery(filters)}`);
export const exportRecords = (filters: RecordFilters): Promise<string> =>
  get(`${recordsPath}/export?${referralQuery(filters)}`, undefined, "text");
export const exportReport = (filters: ReportFilters): Promise<string> =>
  get(`${path}/report/export?${referralQuery(filters)}`, undefined, "text");
